import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Select,
  Typography,
  Card,
  message,
  Space,
  Tag,
  Modal,
  Form,
  DatePicker,
  Input,
  Row,
  Col,
  Divider,
  Popconfirm,
  Switch,
} from 'antd';
import {
  SyncOutlined,
  EditOutlined,
  LockOutlined,
  UnlockOutlined,
  SaveOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useCompany } from '../../contexts/CompanyContext';
import { useUser } from '../../contexts/UserContext';
import { useSecureRequest } from '../../hooks/useSecureRequest';
import { AccountItem, AccountingConfigItem, AccountingPeriodLock, AccountingCompanySettings } from '../../services/accountingService';

const { Title, Text } = Typography;
const { Option } = Select;

const CONFIG_DESCRIPTIONS: Record<string, { title: string; desc: string; category: string; color: string }> = {
  DEFAULT_CASH: {
    title: 'Caja General / Pagos en Efectivo',
    desc: 'Se debita al recibir cobros en efectivo y se acredita al pagar gastos de contado.',
    category: 'Tesorería',
    color: 'blue',
  },
  DEFAULT_BANK: {
    title: 'Bancos / Cuentas Corrientes y de Ahorros',
    desc: 'Cuenta de depósitos para transferencias bancarias, pasarelas y pagos electrónicos.',
    category: 'Tesorería',
    color: 'blue',
  },
  DEFAULT_RECEIVABLE: {
    title: 'Clientes Nacionales (Cuentas por Cobrar)',
    desc: 'Se debita al emitir facturas de venta a crédito y se acredita al registrar recibos de caja.',
    category: 'Cartera',
    color: 'green',
  },
  DEFAULT_PAYABLE: {
    title: 'Proveedores Nacionales (Cuentas por Pagar)',
    desc: 'Se acredita al recibir compras a crédito y se debita al realizar egresos/pagos.',
    category: 'Proveedores',
    color: 'orange',
  },
  DEFAULT_INVENTORY: {
    title: 'Inventario de Mercancías / Insumos',
    desc: 'Se debita en compras de inventario e insumos, y se acredita al descargar por ventas.',
    category: 'Inventario',
    color: 'purple',
  },
  DEFAULT_FINISHED_GOODS: {
    title: 'Inventario de Productos Terminados / Cosecha',
    desc: 'Almacena la producción terminada de cosechas o manufactura lista para venta.',
    category: 'Inventario',
    color: 'purple',
  },
  DEFAULT_GOODS_IN_TRANSIT: {
    title: 'Mercancías en Tránsito / Pendientes por Recibir',
    desc: 'Se debita al radicar facturas en compras diferidas y se traslada a Inventario Disponible (1435) al registrar la entrada física a bodega.',
    category: 'Inventario',
    color: 'purple',
  },
  DEFAULT_SALES_REVENUE: {
    title: 'Ingresos por Ventas Operacionales',
    desc: 'Se acredita al facturar ventas de mercancías o bienes gravados/exentos.',
    category: 'Ingresos',
    color: 'cyan',
  },
  DEFAULT_SERVICES_EXPENSE: {
    title: 'Gastos de Administración y Servicios',
    desc: 'Se debita al registrar compras de servicios operativos o gastos generales.',
    category: 'Gastos',
    color: 'magenta',
  },
  DEFAULT_COGS: {
    title: 'Costo de Ventas de Mercancías',
    desc: 'Se debita al asentar el costo de adquisición de las mercancías vendidas.',
    category: 'Costos',
    color: 'volcano',
  },
  DEFAULT_VAT_PURCHASES: {
    title: 'Impuesto a las Ventas (IVA) Descontable',
    desc: 'Se debita por el IVA soportado en facturas de compras a proveedores.',
    category: 'Impuestos',
    color: 'red',
  },
  DEFAULT_VAT_SALES: {
    title: 'Impuesto a las Ventas (IVA) Generado',
    desc: 'Se acredita por el valor del IVA facturado en ventas a clientes.',
    category: 'Impuestos',
    color: 'red',
  },
  DEFAULT_WITHHOLDING: {
    title: 'Retención en la Fuente por Pagar (Compras)',
    desc: 'Se acredita por las retenciones practicadas a proveedores en compras sujetas.',
    category: 'Impuestos',
    color: 'red',
  },
  DEFAULT_PROD_INPUTS: {
    title: 'Costos de Producción - Insumos Directos',
    desc: 'Se debita por materiales, semillas o insumos aplicados directamente al ciclo.',
    category: 'Producción',
    color: 'geekblue',
  },
  DEFAULT_PROD_LABOR: {
    title: 'Costos de Producción - Mano de Obra y Servicios',
    desc: 'Se debita por jornales, mano de obra directa y labores aplicadas a la producción.',
    category: 'Producción',
    color: 'geekblue',
  },
  DEFAULT_PROD_CLOSING: {
    title: 'Costos de Producción - Cierre y Transferencia',
    desc: 'Se acredita al liquidar el ciclo de producción y transferir su costo al inventario.',
    category: 'Producción',
    color: 'geekblue',
  },
  DEFAULT_EQUITY: {
    title: 'Capital Social / Aportes de Socios',
    desc: 'Se acredita en inventarios iniciales o aportes en especie de socios y accionistas.',
    category: 'Patrimonio',
    color: 'gold',
  },
  DEFAULT_SALES_RETURNS: {
    title: 'Devoluciones y Descuentos en Ventas',
    desc: 'Se debita al registrar devoluciones de clientes en notas crédito o cancelaciones de venta.',
    category: 'Ingresos',
    color: 'cyan',
  },
  DEFAULT_OTHER_INCOME: {
    title: 'Otros Ingresos / Sobrantes de Inventario',
    desc: 'Se acredita al ajustar inventarios físicamente con sobrantes o regularizaciones positivas.',
    category: 'Ingresos',
    color: 'cyan',
  },
  DEFAULT_INVENTORY_LOSS: {
    title: 'Gastos por Pérdida y Mermas de Inventario',
    desc: 'Se debita por bajas de inventario, vencimientos, daños o faltantes en recuentos físicos.',
    category: 'Gastos',
    color: 'magenta',
  },
  // Legacy aliases
  DEFAULT_CASH_ACCOUNT: {
    title: 'Caja General / Pagos en Efectivo',
    desc: 'Se debita al recibir cobros en efectivo y se acredita al pagar gastos de contado.',
    category: 'Tesorería',
    color: 'blue',
  },
  DEFAULT_BANK_ACCOUNT: {
    title: 'Bancos / Transferencias',
    desc: 'Cuenta de depósitos para transferencias bancarias, pasarelas y pagos electrónicos.',
    category: 'Tesorería',
    color: 'blue',
  },
  DEFAULT_AR_ACCOUNT: {
    title: 'Clientes Nacionales (Cuentas por Cobrar)',
    desc: 'Se debita al emitir facturas de venta a crédito y se acredita al registrar recibos de caja.',
    category: 'Cartera',
    color: 'green',
  },
  DEFAULT_AP_ACCOUNT: {
    title: 'Proveedores Nacionales (Cuentas por Pagar)',
    desc: 'Se acredita al recibir compras a crédito y se debita al realizar egresos/pagos.',
    category: 'Proveedores',
    color: 'orange',
  },
  DEFAULT_INVENTORY_ACCOUNT: {
    title: 'Inventario de Mercancías (1435)',
    desc: 'Se debita en compras de inventario y se acredita en el descargue por costo de venta.',
    category: 'Inventario',
    color: 'purple',
  },
  DEFAULT_SALES_REVENUE_ACCOUNT: {
    title: 'Ingresos por Venta de Bienes (4135)',
    desc: 'Se acredita al facturar productos tangibles y mercancías gravadas/exentas.',
    category: 'Ingresos',
    color: 'cyan',
  },
  DEFAULT_SERVICES_REVENUE_ACCOUNT: {
    title: 'Ingresos por Prestación de Servicios (4145)',
    desc: 'Se acredita al facturar servicios técnicos, asesorías y mano de obra intangible.',
    category: 'Ingresos',
    color: 'cyan',
  },
  DEFAULT_COST_OF_GOODS_ACCOUNT: {
    title: 'Costo de Ventas de Mercancías (6135)',
    desc: 'Se debita al asentar el costo promedio de las mercancías entregadas en ventas.',
    category: 'Costos',
    color: 'volcano',
  },
  DEFAULT_IVA_GENERATED_ACCOUNT: {
    title: 'Impuesto a las Ventas (IVA) Generado (240801)',
    desc: 'Se acredita por el valor del IVA facturado en ventas a clientes.',
    category: 'Impuestos',
    color: 'red',
  },
  DEFAULT_IVA_DISCOUNTABLE_ACCOUNT: {
    title: 'Impuesto a las Ventas (IVA) Descontable (240802)',
    desc: 'Se debita por el IVA soportado en facturas de compras a proveedores.',
    category: 'Impuestos',
    color: 'red',
  },
  DEFAULT_RETEFUENTE_SALES_ACCOUNT: {
    title: 'Anticipo Retefuente en Ventas (135515)',
    desc: 'Se debita por la retención en la fuente practicada por clientes autorretenedores.',
    category: 'Impuestos',
    color: 'red',
  },
  DEFAULT_RETEFUENTE_PURCHASES_ACCOUNT: {
    title: 'Retención en la Fuente por Pagar (236540)',
    desc: 'Se acredita por las retenciones practicadas a proveedores en compras sujetas.',
    category: 'Impuestos',
    color: 'red',
  },
};

const getConfigKey = (item?: AccountingConfigItem | null): string => {
  if (!item) return '';
  return item.account_key || item.config_key || '';
};

const AccountingSettingsView: React.FC = () => {
  const { company } = useCompany();
  const { user } = useUser();
  const { securePost, loading } = useSecureRequest();

  const [configs, setConfigs] = useState<AccountingConfigItem[]>([]);
  const [accounts, setAccounts] = useState<AccountItem[]>([]);
  const [editingItem, setEditingItem] = useState<AccountingConfigItem | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();

  // Period Lock state
  const [periodLock, setPeriodLock] = useState<AccountingPeriodLock | null>(null);
  const [lockForm] = Form.useForm();
  const [savingLock, setSavingLock] = useState(false);

  // Automation Parameters state
  const [autoSettings, setAutoSettings] = useState<AccountingCompanySettings>({
    company_id: company.current.id,
    auto_accounting_enabled: false,
  });
  const [savingAutoSettings, setSavingAutoSettings] = useState(false);

  const loadData = async () => {
    if (!company.current.id) return;
    try {
      const [cfgRes, accRes, lockRes, setRes] = await Promise.all([
        securePost('/accounting/configs', { company_id: company.current.id }),
        securePost('/accounting/accounts', { company_id: company.current.id }),
        securePost('/accounting/period-lock', { company_id: company.current.id }),
        securePost('/accounting/settings', { company_id: company.current.id }),
      ]);
      if (cfgRes?.data) setConfigs(cfgRes.data);
      if (accRes?.data) setAccounts(accRes.data);
      if (lockRes?.data) {
        const rawLock = lockRes.data?.lock || lockRes.data;
        const lk = rawLock?.lock_date ? rawLock : null;
        setPeriodLock(lk);
        lockForm.setFieldsValue({
          lock_date: lk?.lock_date ? dayjs(lk.lock_date) : null,
          notes: lk?.notes || '',
        });
      }
      if (setRes?.data && (!setRes?.status || setRes.status === 200) && !setRes?.error) {
        const st = setRes.data?.settings || setRes.data;
        setAutoSettings({
          company_id: company.current.id,
          auto_accounting_enabled: st?.auto_accounting_enabled ?? false,
          updated_by: st?.updated_by ?? null,
          updated_at: st?.updated_at ?? null,
        });
      }
    } catch (err) {
      console.error('Error loading accounting configs:', err);
      message.error('No se pudieron cargar las configuraciones contables.');
    }
  };

  const handleToggleAutoSetting = async (value: boolean) => {
    const previousSettings = { ...autoSettings };
    try {
      setSavingAutoSettings(true);
      const updated = {
        ...autoSettings,
        auto_accounting_enabled: value,
      };
      setAutoSettings(updated);

      const res = await securePost('/accounting/settings', {
        company_id: company.current.id,
        auto_accounting_enabled: value,
        user_id: user?.id || null,
        updated_by: user?.id || null,
      });

      const isError =
        (res?.status && res.status !== 200) ||
        Boolean(res?.error) ||
        Boolean(res?.data?.error) ||
        res?.success === false ||
        res?.data?.success === false;

      if (isError) {
        const errorMsg =
          res?.message ||
          res?.data?.message ||
          res?.error ||
          res?.data?.error ||
          'Error al guardar los parámetros de contabilización.';
        throw new Error(errorMsg);
      }

      const st = res?.settings || res?.data?.settings || res?.data;
      if (st && typeof st === 'object') {
        setAutoSettings({
          company_id: company.current.id,
          auto_accounting_enabled: st.auto_accounting_enabled ?? updated.auto_accounting_enabled,
          updated_by: st.updated_by ?? null,
          updated_at: st.updated_at ?? null,
        });
      }

      message.success(res?.message || 'Parámetros de contabilización automática actualizados con éxito.');
    } catch (err: any) {
      console.error('Error saving auto accounting settings:', err);
      setAutoSettings(previousSettings);
      message.error(err?.message || 'Error al guardar los parámetros de contabilización.');
      void loadData(); // Revert on error
    } finally {
      setSavingAutoSettings(false);
    }
  };

  useEffect(() => {
    if (company.current.id) {
      void loadData();
    }
  }, [company.current.id]);

  const handleSavePeriodLock = async (unlock = false) => {
    try {
      setSavingLock(true);
      let payloadDate: string | null = null;
      let notes = '';

      if (!unlock) {
        const vals = await lockForm.validateFields();
        if (!vals.lock_date) {
          message.warning('Seleccione una fecha de bloqueo válida.');
          return;
        }
        payloadDate = vals.lock_date.format('YYYY-MM-DD');
        notes = vals.notes || '';
      }

      const res = await securePost('/accounting/period-lock', {
        company_id: company.current.id,
        lock_date: payloadDate,
        notes: notes,
      });

      const isError =
        (res?.status && res.status !== 200) ||
        Boolean(res?.error) ||
        Boolean(res?.data?.error) ||
        res?.success === false ||
        res?.data?.success === false;

      if (isError) {
        throw new Error(
          res?.message ||
          res?.data?.message ||
          res?.error ||
          res?.data?.error ||
          'Error al actualizar el bloqueo de periodo.'
        );
      }

      if (unlock) {
        message.success('Periodo contable desbloqueado con éxito.');
        setPeriodLock(null);
        lockForm.resetFields();
      } else {
        const savedLock = res?.data?.lock || res?.data || { lock_date: payloadDate, notes };
        message.success(`Periodo bloqueado con éxito hasta el ${dayjs(payloadDate).format('DD/MM/YYYY')}.`);
        setPeriodLock(savedLock);
      }
    } catch (err: any) {
      console.error('Error saving period lock:', err);
      message.error(err?.message || 'Error al actualizar el bloqueo de periodo.');
    } finally {
      setSavingLock(false);
    }
  };

  const handleOpenEdit = (item: AccountingConfigItem) => {
    setEditingItem(item);
    form.setFieldsValue({
      account_id: item.account_id,
    });
    setModalVisible(true);
  };

  const handleSaveConfig = async () => {
    try {
      const values = await form.validateFields();
      if (!editingItem) return;

      const key = getConfigKey(editingItem);
      const res = await securePost('/accounting/configs', {
        company_id: company.current.id,
        account_key: key,
        config_key: key,
        account_id: values.account_id,
      });

      const isError =
        (res?.status && res.status !== 200) ||
        Boolean(res?.error) ||
        Boolean(res?.data?.error) ||
        res?.success === false ||
        res?.data?.success === false;

      if (isError) {
        throw new Error(
          res?.message ||
          res?.data?.message ||
          res?.error ||
          res?.data?.error ||
          'Error al guardar la configuración.'
        );
      }

      const meta = CONFIG_DESCRIPTIONS[key];
      message.success(`Cuenta predeterminada para ${meta?.title || key} actualizada.`);
      setModalVisible(false);
      void loadData();
    } catch (err: any) {
      console.error('Error saving config:', err);
      message.error(err?.message || 'Error al guardar la configuración.');
    }
  };

  const columns = [
    {
      title: 'Categoría',
      key: 'category',
      width: 130,
      render: (_: any, r: AccountingConfigItem) => {
        const key = getConfigKey(r);
        const meta = CONFIG_DESCRIPTIONS[key];
        return <Tag color={meta?.color || 'blue'}>{meta?.category || 'General'}</Tag>;
      },
    },
    {
      title: 'Flujo / Concepto Operacional',
      key: 'concept',
      render: (_: any, r: AccountingConfigItem) => {
        const key = getConfigKey(r);
        const meta = CONFIG_DESCRIPTIONS[key];
        return (
          <div>
            <Text strong style={{ fontSize: 14 }}>{meta?.title || key}</Text>
            {meta?.desc && <div><Text type="secondary" style={{ fontSize: 12 }}>{meta.desc}</Text></div>}
            <div><Text code style={{ fontSize: 11 }}>{key}</Text></div>
          </div>
        );
      },
    },
    {
      title: 'Cuenta Asignada en PUC',
      key: 'account',
      width: 320,
      render: (_: any, r: AccountingConfigItem) => {
        const acc = accounts.find(a => a.id === r.account_id) || r.accounting_accounts;
        if (!acc) return <Tag color="red">Sin Asignar</Tag>;
        return (
          <div>
            <Text strong style={{ color: '#1677ff' }}>{acc.code}</Text>
            <div><Text>{acc.name}</Text></div>
          </div>
        );
      },
    },
    {
      title: 'Acción',
      key: 'action',
      width: 100,
      align: 'center' as const,
      render: (_: any, r: AccountingConfigItem) => (
        <Button
          type="primary"
          ghost
          icon={<EditOutlined />}
          onClick={() => handleOpenEdit(r)}
          size="small"
        >
          Cambiar
        </Button>
      ),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>Cuentas Contables Predeterminadas</Title>
          <Text type="secondary">Mapeo automático de cuentas para facturación, compras, inventario y tesorería</Text>
        </div>
        <Space wrap>
          <Button icon={<SyncOutlined spin={loading} />} onClick={loadData}>
            Refrescar
          </Button>
        </Space>
      </div>

      {/* Automation Parameters Section */}
      <Card
        bordered={false}
        style={{
          borderRadius: 8,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
          borderLeft: autoSettings.auto_accounting_enabled ? '4px solid #1677ff' : '4px solid #faad14',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
          <div style={{ flex: 1, minWidth: 280 }}>
            <Space align="center">
              <Title level={4} style={{ margin: 0 }}>
                <SettingOutlined style={{ color: autoSettings.auto_accounting_enabled ? '#1677ff' : '#faad14' }} />
                {' '}Contabilización Automática General
              </Title>
              <Tag color={autoSettings.auto_accounting_enabled ? 'processing' : 'warning'}>
                {autoSettings.auto_accounting_enabled ? 'Activa' : 'Pausada'}
              </Tag>
            </Space>
            <div style={{ marginTop: 6 }}>
              <Text type="secondary">
                Controla la generación automática de comprobantes contables en tiempo real para compras, ventas, cobros, traslados, etc.
                Al pausarla, los nuevos movimientos quedan registrados como <Text code>No Aplica</Text> para no generar pendientes ficticios ni alterar los libros contables.
              </Text>
            </div>
            {autoSettings.updated_at && (
              <div style={{ marginTop: 6 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Última actualización: {dayjs(autoSettings.updated_at).format('DD/MM/YYYY HH:mm')}
                </Text>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Switch
              checked={autoSettings.auto_accounting_enabled}
              loading={savingAutoSettings}
              onChange={handleToggleAutoSetting}
              checkedChildren="Activa"
              unCheckedChildren="Pausada"
            />
          </div>
        </div>
      </Card>

      {/* Period Lock Section */}
      <Card
        bordered={false}
        style={{
          borderRadius: 8,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
          borderLeft: periodLock?.lock_date ? '4px solid #ff4d4f' : '4px solid #52c41a',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <Space align="center">
              <Title level={4} style={{ margin: 0 }}>
                {periodLock?.lock_date ? <LockOutlined style={{ color: '#ff4d4f' }} /> : <UnlockOutlined style={{ color: '#52c41a' }} />}
                {' '}Cierre y Bloqueo de Periodos Contables
              </Title>
              {periodLock?.lock_date ? (
                <Tag color="error">
                  Bloqueado hasta: {dayjs(periodLock.lock_date).format('DD/MM/YYYY')}
                </Tag>
              ) : (
                <Tag color="success">Sin bloqueo activo (Periodos abiertos)</Tag>
              )}
            </Space>
            <div style={{ marginTop: 6 }}>
              <Text type="secondary">
                Impide que los usuarios creen, modifiquen o anulen comprobantes y movimientos con fecha igual o anterior a la indicada. Úselo para blindar periodos ya conciliados o declarados ante la DIAN.
              </Text>
            </div>
          </div>
        </div>

        <Divider style={{ margin: '16px 0' }} />

        <Form form={lockForm} layout="vertical">
          <Row gutter={16} align="bottom">
            <Col xs={24} sm={10} md={8}>
              <Form.Item
                name="lock_date"
                label={<Text strong>Fecha Límite de Bloqueo (Inclusive)</Text>}
                tooltip="Todas las transacciones con fecha menor o igual a este día quedarán blindadas contra modificaciones o nuevos registros."
              >
                <DatePicker
                  format="DD/MM/YYYY"
                  placeholder="Seleccione fecha de corte"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={14} md={10}>
              <Form.Item
                name="notes"
                label={<Text strong>Motivo / Observaciones del Cierre</Text>}
              >
                <Input
                  placeholder="Ej. Cierre tributario IVA y Retefuente Enero 2026"
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={6}>
              <Form.Item>
                <Space wrap>
                  <Button
                    type="primary"
                    danger={!!periodLock?.lock_date}
                    icon={<SaveOutlined />}
                    onClick={() => handleSavePeriodLock(false)}
                    loading={savingLock}
                  >
                    {periodLock?.lock_date ? 'Actualizar Bloqueo' : 'Fijar Bloqueo'}
                  </Button>
                  {periodLock?.lock_date && (
                    <Popconfirm
                      title="¿Desea desbloquear el periodo contable?"
                      description="Se permitirá registrar y modificar transacciones en cualquier fecha pasada."
                      onConfirm={() => handleSavePeriodLock(true)}
                      okText="Sí, desbloquear"
                      cancelText="Cancelar"
                      okButtonProps={{ danger: true }}
                    >
                      <Button icon={<UnlockOutlined />} loading={savingLock}>
                        Desbloquear
                      </Button>
                    </Popconfirm>
                  )}
                </Space>
              </Form.Item>
            </Col>
          </Row>
        </Form>
        {periodLock?.updated_at && (
          <div style={{ marginTop: -8 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Última actualización de bloqueo: {dayjs(periodLock.updated_at).format('DD/MM/YYYY hh:mm A')}
            </Text>
          </div>
        )}
      </Card>

      {/* Config Table */}
      <Card bordered={false} style={{ borderRadius: 8 }}>
        <div style={{ marginBottom: 16 }}>
          <Title level={4} style={{ margin: 0 }}>Cuentas Contables Predeterminadas</Title>
          <Text type="secondary">Asignación de cuentas del PUC para operaciones automáticas del sistema</Text>
        </div>
        <Table
          dataSource={configs}
          columns={columns}
          rowKey={(r) => r.id || getConfigKey(r)}
          loading={loading}
          pagination={false}
          size="middle"
        />
      </Card>

      {/* Edit Modal */}
      <Modal
        title={`Modificar Cuenta: ${CONFIG_DESCRIPTIONS[getConfigKey(editingItem)]?.title || getConfigKey(editingItem)}`}
        open={modalVisible}
        onOk={handleSaveConfig}
        onCancel={() => setModalVisible(false)}
        okText="Guardar Asignación"
        cancelText="Cancelar"
        confirmLoading={loading}
        width={550}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <div style={{ marginBottom: 16, background: '#f5f5f5', padding: '10px 14px', borderRadius: 6 }}>
            <Text strong>{CONFIG_DESCRIPTIONS[getConfigKey(editingItem)]?.title || getConfigKey(editingItem)}</Text>
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {CONFIG_DESCRIPTIONS[getConfigKey(editingItem)]?.desc}
              </Text>
            </div>
          </div>

          <Form.Item
            name="account_id"
            label="Seleccione la Cuenta Contable del PUC"
            rules={[{ required: true, message: 'Seleccione una cuenta contable' }]}
          >
            <Select
              placeholder="Buscar por código o nombre..."
              showSearch
              optionFilterProp="children"
              style={{ width: '100%' }}
            >
              {accounts.map(acc => (
                <Option key={acc.id} value={acc.id}>
                  {acc.code} - {acc.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AccountingSettingsView;
